// frontend/src/pages/Home.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import { getServices, getRealizations } from "../services/api";
import ImageZoom from "../components/ImageZoom";

const API_URL = "https://api.seengineering-tn.com";

const Home = () => {
  const [services, setServices] = useState([]);
  const [realizations, setRealizations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, realizationsRes] = await Promise.all([
          getServices({ published: true }),
          getRealizations({ published: true }),
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setRealizations(realizationsRes.data.slice(0, 6));
      } catch (error) {
        console.error("Erreur de chargement:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Solutions d'Ingénierie Innovantes
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Transformez vos idées en réalité avec notre expertise technique
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
              >
                Nos Services
                <FaArrowRight />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all inline-flex items-center justify-center gap-2"
              >
                Demander un Devis
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Nos Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Découvrez notre gamme complète de services d'ingénierie adaptés à
              vos besoins
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service._id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden"
              >
                <ImageZoom
                  src={`${API_URL}${service.image}`}
                  alt={service.title}
                  className="h-48"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {service.description}
                  </p>
                  {/* <Link
                    to={`/services/${service._id}`}
                    className="text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center gap-2"
                  >
                    En savoir plus
                    <FaArrowRight size={14} />
                  </Link> */}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Voir tous les services
            </Link>
          </div>
        </div>
      </section>

      {/* Realizations Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Nos Réalisations
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Découvrez quelques-uns de nos projets récents
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {realizations.map((realization) => (
              <div
                key={realization._id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden"
              >
                <ImageZoom
                  src={`${
                    process.env.REACT_APP_API_URL ||
                    "https://api.seengineering-tn.com"
                  }${realization.image}`}
                  alt={realization.title}
                  className="h-48"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {realization.title}
                  </h3>
                  {realization.client && (
                    <p className="text-sm text-blue-600 mb-2">
                      Client: {realization.client}
                    </p>
                  )}
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {realization.description}
                  </p>
                  {/* <Link
                    to={`/realizations/${realization._id}`}
                    className="text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center gap-2"
                  >
                    Voir le projet
                    <FaArrowRight size={14} />
                  </Link> */}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/realizations"
              className="inline-block px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Voir toutes les réalisations
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Pourquoi Nous Choisir ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Expertise Technique",
                desc: "Une équipe d'experts qualifiés",
              },
              {
                title: "Solutions Personnalisées",
                desc: "Adaptées à vos besoins spécifiques",
              },
              {
                title: "Qualité Garantie",
                desc: "Standards élevés de qualité",
              },
              {
                title: "Support Continu",
                desc: "Accompagnement à chaque étape",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaCheck className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à Démarrer Votre Projet ?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Contactez-nous dès aujourd'hui pour discuter de vos besoins
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Nous Contacter
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
